/**
 * hooks/useFeatureFlags.js — Phase 81.5.4 · F5
 *
 * Small React hook that fetches server-authoritative feature flags from
 * GET /api/config/flags exactly once at mount and caches them for the
 * app lifetime.
 *
 * The server is the single source of truth. This hook DOES NOT read
 * any client-side env var to decide flag state — that decision belongs
 * on the backend (`lib/feature_flags.py`), which considers server env
 * + deployment identity.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Module-level cache so components mounted after the first fetch reuse
// the same result without a second network call.
let _cache = null;
let _inflight = null;

const DEFAULT_FLAGS = {
  'imports.single_authority_ui': false,
};

async function fetchFlagsOnce() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/config/flags`, {
        timeout: 5000,
      });
      _cache = { ...DEFAULT_FLAGS, ...(r?.data?.flags || {}) };
      return _cache;
    } catch {
      // Any failure → default all flags to OFF. Safe rollback behavior.
      _cache = { ...DEFAULT_FLAGS };
      return _cache;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

export function useFeatureFlags() {
  const [flags, setFlags] = useState(_cache || DEFAULT_FLAGS);
  const [loaded, setLoaded] = useState(!!_cache);
  useEffect(() => {
    let alive = true;
    fetchFlagsOnce().then((f) => {
      if (!alive) return;
      setFlags(f);
      setLoaded(true);
    });
    return () => { alive = false; };
  }, []);
  return { flags, loaded };
}

/** Synchronous accessor — returns whatever's cached, DEFAULT_FLAGS if not loaded yet. */
export function getFlagsSync() {
  return _cache || DEFAULT_FLAGS;
}

/** For tests / manual override in dev tools. */
export function _resetFlagsCache() {
  _cache = null;
  _inflight = null;
}
